import { useMemo, useState } from "react";
import withRouter from "hoc/withRouter";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  MdArrowForward,
  MdAssignmentTurnedIn,
  MdCalendarToday,
  MdConfirmationNumber,
  MdInbox,
  MdInventory2,
  MdPerson,
  MdTrain,
} from "react-icons/md";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import baggageService from "services/admin/baggageService";
import { snack } from "components/Snack/Snack";
import {
  baggageStatusColor,
  baggageStatusLabel,
} from "pages/Admin/Baggages/Baggage/baggage-status";

// Имя пассажира из связанного клиента отправки (как в списке отправок).
const customerName = (customer: any) =>
  customer
    ? `${customer.lastName || ""} ${customer.firstName || ""}`.trim() || customer.name || "—"
    : "—";

/** Строка «иконка — подпись — значение» для реквизитов отправки. Корпоративный стиль раздела. */
const InfoRow = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, py: 1 }}>
    <Box sx={{ color: "text.secondary", display: "flex", mt: "2px", fontSize: 20 }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 500, wordBreak: "break-word" }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

interface TripGroup {
  trip: any;
  tripID: any;
  pieces: any[];
}

const ArrivalsList = () => {
  const orgID = localStorage.getItem("orgID") || "";
  const [deliveringId, setDeliveringId] = useState<any>(null);

  const {
    data: baggageResponse,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["baggage-arrivals", { orgID }],
    queryFn: async () => {
      // Прибывшие места — статус arrived. Сервер уже возвращает их вместе с отправкой.
      const response: any = await baggageService.getAll({ status: "arrived" });
      return response.data;
    },
  });

  // Группируем прибывшие места по отправке — на странице одна карточка = одна отправка.
  const groups: TripGroup[] = useMemo(() => {
    const rows: any[] = (baggageResponse as any)?.data || [];
    const map = new Map<any, TripGroup>();
    for (const b of rows) {
      const tripID = b.tripID ?? b.trip?.id;
      if (!map.has(tripID)) {
        map.set(tripID, { trip: b.trip, tripID, pieces: [] });
      }
      map.get(tripID)!.pieces.push(b);
    }
    return Array.from(map.values());
  }, [baggageResponse]);

  const totalPieces = groups.reduce((s, g) => s + g.pieces.length, 0);

  // Выдача всей отправки: каждое прибывшее место переводим arrived → delivered через скан по бирке.
  const handleDeliver = async (group: TripGroup) => {
    const recipient = group.trip?.recipientName;
    if (
      !window.confirm(
        `Выдать багаж по отправке #${group.tripID}? Мест к выдаче: ${group.pieces.length}.`,
      )
    ) {
      return;
    }
    setDeliveringId(group.tripID);
    try {
      await Promise.all(
        group.pieces.map((p: any) =>
          baggageService.scan({
            tagCode: p.tagCode,
            status: "delivered",
            stationID: group.trip?.destinationStationID || null,
            note: recipient ? `Выдача багажа — ${recipient}` : "Выдача багажа получателю",
          }),
        ),
      );
      snack(`Выдано мест: ${group.pieces.length}`);
      refetch();
    } catch (ex) {
      // Ошибки (недопустимый переход / не найдено) показывает axios-интерсептор.
      console.error(ex);
    } finally {
      setDeliveringId(null);
    }
  };

  function buildContent() {
    if (isLoading) {
      return (
        <div className="text-center">
          <CircularProgress />
        </div>
      );
    }

    if (groups.length === 0) {
      return (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
              <MdInbox size={48} style={{ opacity: 0.4 }} />
              <Typography variant="h6" sx={{ mt: 1, fontWeight: 600 }}>
                Прибывших багажей нет
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Здесь появятся отправки, чей багаж прибыл на станцию назначения и готов к выдаче.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {groups.map((group) => {
          const { trip } = group;
          const delivering = deliveringId === group.tripID;
          return (
            <Card key={group.tripID} variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                {/* Шапка: маршрут + счётчик прибывших мест */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Откуда
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {trip?.originStation?.name || "—"}
                      </Typography>
                    </Box>
                    <Box sx={{ color: "primary.main", display: "flex", fontSize: 26 }}>
                      <MdArrowForward />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Куда
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {trip?.destinationStation?.name || "—"}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    color="warning"
                    icon={<MdInventory2 />}
                    label={`Прибыло мест: ${group.pieces.length}`}
                    sx={{ fontWeight: 600 }}
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Реквизиты отправки */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    columnGap: 2,
                  }}
                >
                  <InfoRow icon={<MdPerson />} label="Клиент" value={customerName(trip?.customer)} />
                  <InfoRow icon={<MdTrain />} label="Поезд" value={trip?.train?.number || "—"} />
                  <InfoRow
                    icon={<MdCalendarToday />}
                    label="Дата отправления"
                    value={trip?.departureDate || "—"}
                  />
                  <InfoRow
                    icon={<MdConfirmationNumber />}
                    label="Номер билета"
                    value={trip?.ticketNumber || "—"}
                  />
                </Box>

                {/* Прибывшие места */}
                <Box sx={{ mt: 1.5 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Бирка</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Тип</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Вес</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Статус</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.pieces.map((p: any) => (
                        <TableRow key={p.id} hover>
                          <TableCell sx={{ fontWeight: 600, fontFamily: "monospace" }}>
                            <Link to={`/admin/baggages/detail/${p.id}`}>{p.tagCode}</Link>
                          </TableCell>
                          <TableCell>{p.type || "—"}</TableCell>
                          <TableCell>{p.weight != null ? `${Number(p.weight)} кг` : "—"}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              variant="outlined"
                              color={baggageStatusColor(p.status)}
                              label={baggageStatusLabel(p.status)}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Действия */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Link to={`/admin/baggages/baggage-trips/detail/${group.tripID}`}>
                    <Button variant="outlined">Открыть отправку</Button>
                  </Link>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<MdAssignmentTurnedIn />}
                    disabled={delivering}
                    onClick={() => handleDeliver(group)}
                  >
                    {delivering ? "Выдаём..." : "Выдать"}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  }

  return (
    <div>
      <div
        className="flex gap-2 items-center m-b-16"
        style={{ flexWrap: "wrap", justifyContent: "space-between" }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <h2 className="text-2xl">Прибывшие багажи</h2>
            {!isLoading && groups.length > 0 && (
              <Chip
                size="small"
                color="warning"
                label={`${groups.length} отпр. · ${totalPieces} мест`}
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Багаж, прибывший на станцию назначения и готовый к выдаче получателю.
          </Typography>
        </Box>
        <Button onClick={() => window.history.back()}>Назад</Button>
      </div>
      <div className="m-t-32">{buildContent()}</div>
    </div>
  );
};

export default withRouter(ArrivalsList);
